import React, { Fragment, useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../common/breadcrumb";
import { Card, CardBody, Container, Row } from "reactstrap";
import { Button } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { Refresh } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import CommonLoader from "../../common/commonLoader";
import CommonTableApBulkInvite from "../../common/commonTableApBulkInvite";
import {
  getAllVendorDocumentUploadList,
  replyVendorDocument,
} from "../../api/ap_services";
import VendorDocumentReplyModal from "./vendorDocumentReplyModal";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  btnClass: {
    padding: "0.5rem 1.5rem!important",
    fontWeight: 600,
  },
  cardbody: {
    padding: `${theme.spacing(1.8, 1.3)} !important`,
    [theme.breakpoints.down("sm")]: {
      padding: `${theme.spacing(0)} !important`,
      margin: `${theme.spacing(0, 1)} !important`,
    },
  },
}));

const normalizeRow = (row) => ({
  ...row,
  vendorDocumentUploadId:
    row.vendorDocumentUploadId ?? row.vendor_document_upload_id,
  vendorCode: row.vendorCode ?? row.vendor_code ?? "",
  ownerEmail: row.ownerEmail ?? row.owner_email ?? "",
  primaryEmail: row.primaryEmail ?? row.primary_email ?? "",
  remarks: row.remarks ?? "",
  modenikRemarks: row.modenikRemarks ?? row.modenik_remarks ?? "",
  inspectedBy: row.inspectedBy ?? row.inspected_by ?? "",
  createdBy: row.createdBy ?? row.created_by ?? "",
  createdDt: row.createdDt ?? row.created_dt ?? "",
  updatedBy: row.updatedBy ?? row.updated_by ?? "",
  updatedDt: row.updatedDt ?? row.updated_dt ?? "",
});

const VendorDocumentsList = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyRow, setReplyRow] = useState(null);

  const loadListData = (pageNo = page, pageSize = rowsPerPage) => {
    setLoading(true);
    getAllVendorDocumentUploadList(pageNo, pageSize)
      .then((res) => {
        setLoading(false);
        const raw = res.response.responseObject ?? [];
        setData(Array.isArray(raw) ? raw.map(normalizeRow) : []);
        setTotalCount(res.response.responseMap?.totalCount ?? 0);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Failed to load vendor documents");
      });
  };

  useEffect(() => {
    loadListData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadListData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadListData(0, newRowsPerPage);
  };

  const onOpenReplyModal = (row) => {
    setReplyRow(row);
    setReplyModalOpen(true);
  };

  const onSaveReply = (payload) => {
    if (!payload?.modenikRemarks) {
      toast.warning("Please enter Modenik remarks");
      return;
    }
    const inspectedBy =
      JSON.parse(sessionStorage.getItem("userData") || "{}")?.username ||
      "ADMIN";

    setLoading(true);
    replyVendorDocument({
      vendorDocumentUploadId: payload.vendorDocumentUploadId,
      modenikRemarks: payload.modenikRemarks,
      inspectedBy,
    })
      .then((res) => {
        setLoading(false);
        if (res.response?.responseCode === true) {
          toast.success(res.response.message || "Reply submitted successfully");
          setReplyModalOpen(false);
          setReplyRow(null);
          loadListData(page, rowsPerPage);
        } else {
          toast.error(res.response?.message || "Failed to submit reply");
        }
      })
      .catch(() => {
        setLoading(false);
        toast.error("Failed to submit reply");
      });
  };

  const tableHeaders = useMemo(
    () => [
      "Vendor Code",
      "Owner Email",
      "Primary Email",
      "Remarks",
      "Modenik Remarks",
      "Inspected By",
      "Created By",
      "Created Date",
      "Action",
    ],
    [],
  );

  const tableColumns = useMemo(
    () => [
      {
        name: "vendorCode",
        width: "120px",
        options: {
          customBodyRender: (value, tableMeta) =>
            data[tableMeta.rowIndex]?.vendorCode || "-",
        },
      },
      {
        name: "ownerEmail",
        width: "180px",
        options: {
          customBodyRender: (value, tableMeta) =>
            data[tableMeta.rowIndex]?.ownerEmail || "-",
        },
      },
      {
        name: "primaryEmail",
        width: "180px",
        options: {
          customBodyRender: (value, tableMeta) =>
            data[tableMeta.rowIndex]?.primaryEmail || "-",
        },
      },
      {
        name: "remarks",
        width: "200px",
        options: {
          customBodyRender: (value, tableMeta) =>
            data[tableMeta.rowIndex]?.remarks || "-",
        },
      },
      {
        name: "modenikRemarks",
        width: "200px",
        options: {
          customBodyRender: (value, tableMeta) =>
            data[tableMeta.rowIndex]?.modenikRemarks || "-",
        },
      },
      {
        name: "inspectedBy",
        width: "120px",
        options: {
          customBodyRender: (value, tableMeta) =>
            data[tableMeta.rowIndex]?.inspectedBy || "-",
        },
      },
      {
        name: "createdBy",
        width: "120px",
        options: {
          customBodyRender: (value, tableMeta) =>
            data[tableMeta.rowIndex]?.createdBy || "-",
        },
      },
      {
        name: "createdDt",
        width: "140px",
        options: {
          customBodyRender: (value, tableMeta) => {
            const dt = data[tableMeta.rowIndex]?.createdDt;
            return dt ? moment(dt).format("DD-MM-YYYY HH:mm") : "-";
          },
        },
      },
      {
        name: "action",
        width: "100px",
        options: {
          customBodyRender: (value, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const modenikRemarks = (row?.modenikRemarks ?? "")
              .toString()
              .trim();
            if (modenikRemarks) {
              return "-";
            }
            return (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => onOpenReplyModal(row)}
              >
                Reply
              </Button>
            );
          },
        },
      },
    ],
    [data],
  );

  return (
    <Fragment>
      {loading ? <CommonLoader /> : null}
      <Breadcrumb title="Vendor Uploaded Ledger Documents" parent="" />
      <Container fluid={true}>
        <Row>
          <Card>
            <CardBody className={classes.cardbody}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 12,
                }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  type="button"
                  className={classes.btnClass}
                  onClick={() => loadListData(page, rowsPerPage)}
                >
                  <Refresh />
                </Button>
              </div>
              <CommonTableApBulkInvite
                tableData={data}
                tableHeaders={tableHeaders}
                tableColumns={tableColumns}
                rowsPerPageOptions={[25, 50, 100]}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                displayPagination={true}
                stripedRows={true}
              />
            </CardBody>
          </Card>
        </Row>
      </Container>
      <VendorDocumentReplyModal
        open={replyModalOpen}
        onCancel={() => {
          setReplyModalOpen(false);
          setReplyRow(null);
        }}
        onSave={onSaveReply}
        row={replyRow}
      />
      <ToastContainer autoClose={1000} />
    </Fragment>
  );
};

export default VendorDocumentsList;
